/**
 * features/timetable/useTimetableData.ts
 * ------------------------------------------------------------------
 * Loads the timetable for whoever is looking at it.
 *
 * NOT part of DataContext, deliberately. DataContext subscribes on every page
 * for every user; the timetable is ~1,150 documents that only matter on the
 * timetable screens, so it loads when one of those screens mounts and not
 * before. (CLAUDE.md's rule is about the CORE collections — teachers, pupils,
 * classes — which this does not duplicate.)
 *
 * SCOPE
 *   Staff need the whole picture: any class, any teacher, any room, plus
 *   conflict-free lookups. They read all pointers.
 *
 *   A parent or pupil needs one class. Pointer ids are keyed
 *   "Form 1A-Monday-1", so a documentId() range query fetches exactly that
 *   class's 52 pointers instead of all 832. Slots are then fetched by id.
 *
 * Reads are live (`onSnapshot`), so an alteration made in the authoring app
 * appears here without a reload — which is the point of the two systems
 * sharing one database.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  doc,
  documentId,
  onSnapshot,
  query,
  where,
  orderBy,
  startAt,
  endAt,
  getDocs,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Division } from '@/lib/divisions';
import { useTeachingCycle } from '@/lib/useTeachingCycle';
import type {
  ScheduleSettings,
  TimetablePointer,
  TimetableSlot,
  PointerKey,
  SlotId,
} from '@/lib/timetable';

export const TIMETABLE_COLLECTIONS = {
  slots: 'timetableSlots',
  pointers: 'timetablePointers',
  extensionSlots: 'timetableExtensionSlots',
  schedules: 'timetableSchedules',
  rooms: 'timetableRooms',
} as const;

export interface TimetableData {
  slots: Record<SlotId, TimetableSlot>;
  pointers: Record<PointerKey, TimetablePointer>;
  schedule: ScheduleSettings | null;
  rooms: string[];
  loading: boolean;
  error: string | null;
}

/** Firestore caps an `in`/documentId() query at 30 values. */
const IN_CHUNK = 30;

function chunk<T>(xs: T[], n: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += n) out.push(xs.slice(i, i + n));
  return out;
}

/**
 * @param scopeToClass  a class NAME ("Form 1A") to load just that class, or
 *                      null for the whole school. Parents pass their child's
 *                      class; staff pass null.
 * @param division      which bell schedule to read. Primary and secondary
 *                      keep different bells - thirteen periods against
 *                      eleven, and every period after the second at a
 *                      different time - so the caller resolves it from the
 *                      class or teacher being shown. Defaults to secondary
 *                      for a room grid, which can span both.
 */
export function useTimetableData(
  scopeToClass: string | null,
  division: Division = 'secondary',
): TimetableData {
  /* LKC: load the teaching cycle so every date → cycle-day lookup made by the
     screens below (lib/timetable dayForDate) answers from the school's
     settings. */
  useTeachingCycle();
  const [slots, setSlots] = useState<Record<SlotId, TimetableSlot>>({});
  const [pointers, setPointers] = useState<Record<PointerKey, TimetablePointer>>({});
  const [schedule, setSchedule] = useState<ScheduleSettings | null>(null);
  const [rooms, setRooms] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState({ slots: false, pointers: false, schedule: false });

  // A scoped load fetches slots by id, which is a one-shot read rather than a
  // subscription. Guard against an out-of-order response overwriting a newer
  // one when the class changes while a fetch is in flight.
  const fetchSeq = useRef(0);

  useEffect(() => {
    setReady({ slots: false, pointers: false, schedule: false });
    setError(null);
    const unsubs: Array<() => void> = [];
    const fail = (what: string) => (e: Error) => {
      console.error(`timetable ${what}:`, e);
      setError(`Could not load the timetable (${what}).`);
      setReady((r) => ({ ...r, [what]: true }));
    };

    // --- schedule + rooms -------------------------------------------------
    unsubs.push(
      onSnapshot(
        doc(db, TIMETABLE_COLLECTIONS.schedules, division),
        (d) => {
          setSchedule(d.exists() ? (d.data() as ScheduleSettings) : null);
          setReady((r) => ({ ...r, schedule: true }));
        },
        fail('schedule'),
      ),
    );
    unsubs.push(
      onSnapshot(
        doc(db, TIMETABLE_COLLECTIONS.rooms, 'list'),
        (d) => setRooms(d.exists() ? ((d.data().list as string[]) ?? []) : []),
        () => setRooms([]),
      ),
    );

    if (scopeToClass) {
      /* ---- one class: range-query its pointers, then fetch those slots ---- */
      const seq = ++fetchSeq.current;
      unsubs.push(
        onSnapshot(
          query(
            collection(db, TIMETABLE_COLLECTIONS.pointers),
            orderBy(documentId()),
            startAt(`${scopeToClass}-`),
            // U+F8FF sorts above any printable character, so this bounds the
            // range to keys beginning "<class>-" without enumerating them.
            endAt(`${scopeToClass}-`),
          ),
          async (snap) => {
            const ptrs: Record<PointerKey, TimetablePointer> = {};
            snap.forEach((d) => { ptrs[d.id] = d.data() as TimetablePointer; });
            setPointers(ptrs);
            setReady((r) => ({ ...r, pointers: true }));

            const ids = [...new Set(Object.values(ptrs).map((p) => p.slotId))].filter(Boolean);
            if (!ids.length) {
              if (seq === fetchSeq.current) { setSlots({}); setReady((r) => ({ ...r, slots: true })); }
              return;
            }
            try {
              const got: Record<SlotId, TimetableSlot> = {};
              for (const part of chunk(ids, IN_CHUNK)) {
                const qs = await getDocs(
                  query(collection(db, TIMETABLE_COLLECTIONS.slots), where(documentId(), 'in', part)),
                );
                qs.forEach((d) => { got[d.id] = { ...(d.data() as TimetableSlot), id: d.id }; });
              }
              if (seq === fetchSeq.current) {
                setSlots(got);
                setReady((r) => ({ ...r, slots: true }));
              }
            } catch (e) {
              fail('slots')(e as Error);
            }
          },
          fail('pointers'),
        ),
      );
    } else {
      /* ---- whole school: subscribe to both ---- */
      unsubs.push(
        onSnapshot(
          collection(db, TIMETABLE_COLLECTIONS.slots),
          (snap) => {
            const got: Record<SlotId, TimetableSlot> = {};
            snap.forEach((d) => { got[d.id] = { ...(d.data() as TimetableSlot), id: d.id }; });
            setSlots(got);
            setReady((r) => ({ ...r, slots: true }));
          },
          fail('slots'),
        ),
      );
      unsubs.push(
        onSnapshot(
          collection(db, TIMETABLE_COLLECTIONS.pointers),
          (snap) => {
            const got: Record<PointerKey, TimetablePointer> = {};
            snap.forEach((d) => { got[d.id] = d.data() as TimetablePointer; });
            setPointers(got);
            setReady((r) => ({ ...r, pointers: true }));
          },
          fail('pointers'),
        ),
      );
    }

    return () => unsubs.forEach((u) => u());
  }, [scopeToClass, division]);

  const loading = !(ready.slots && ready.pointers && ready.schedule);
  return useMemo(
    () => ({ slots, pointers, schedule, rooms, loading, error }),
    [slots, pointers, schedule, rooms, loading, error],
  );
}
